import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { WaitlistForm } from '@/components/waitlist/WaitlistForm';
import { Play, Shield, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const App = () => {
  const { t } = useTranslation();
  
  return (
    <Layout>
      <SEOHead 
        title={t('seo.tryApp.title')}
        description={t('seo.tryApp.description')}
        canonicalUrl="https://noq5.ai/app"
      />
      <div className="container px-4 md:px-6 py-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-12">
            <div className="inline-block rounded-lg bg-oli-blue-light px-3 py-1 text-sm text-oli-blue mb-4">
              {t('tryApp.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {t('tryApp.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
              {t('tryApp.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-oli-blue text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('tryApp.step1.title')}</h3>
              <p className="text-gray-600 text-sm">{t('tryApp.step1.description')}</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-oli-blue text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('tryApp.step2.title')}</h3>
              <p className="text-gray-600 text-sm">{t('tryApp.step2.description')}</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-oli-blue text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('tryApp.step3.title')}</h3>
              <p className="text-gray-600 text-sm">{t('tryApp.step3.description')}</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-oli-blue-light/20 to-white p-8 rounded-lg mb-12">
            <h2 className="text-2xl font-bold text-center mb-6">{t('tryApp.whyLove')}</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>{t('tryApp.benefit1')}</strong> {t('tryApp.benefit1Sub')}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>{t('tryApp.benefit2')}</strong> {t('tryApp.benefit2Sub')}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>{t('tryApp.benefit3')}</strong> {t('tryApp.benefit3Sub')}
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>{t('tryApp.benefit4')}</strong> {t('tryApp.benefit4Sub')}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>{t('tryApp.benefit5')}</strong> {t('tryApp.benefit5Sub')}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>{t('tryApp.benefit6')}</strong> {t('tryApp.benefit6Sub')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-md mx-auto">
            <WaitlistForm />
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">
              {t('tryApp.trusted')}
            </p>
            <p className="text-xs text-gray-400">
              {t('tryApp.agreement')}
            </p>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default App;